<template>
  <div :class="isPicker ? 'file-manager file-manager-picker' : columnClassName">
    <column-header v-if="!isPicker" :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="'file-manager-wrap' + (dragOver ? ' file-manager-dragover' : '')" @dragover="onDragOver" @dragleave="dragOver = false" @drop="onDrop">
      <div class="row" v-if="!isPicker">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-images"></i> {{ _('Files') }}</h3>
          <p class="text-muted">{{ _('Images uploaded to the shop. An image can be used in several products, categories and pages.') }}</p>
        </div>
      </div>
      <div class="file-manager-toolbar">
        <input type="search" class="form-control file-manager-search" v-model="q" :placeholder="_('Search by name or title')" />
        <select class="form-control file-manager-filter" v-model="usageFilter">
          <option value="all">{{ _('All images') }}</option>
          <option value="used">{{ _('In use') }}</option>
          <option value="unused">{{ _('Not in use') }}</option>
        </select>
        <select class="form-control file-manager-sort" v-model="sort">
          <option value="id">{{ _('Newest first') }}</option>
          <option value="filename">{{ _('By name') }}</option>
        </select>
        <label class="btn btn-success file-manager-upload">
          <input type="file" accept="image/*" multiple @change="onSelect" />
          <i class="fa fa-fw fa-upload"></i> {{ _('Send images') }}
        </label>
      </div>
      <upload-progress :uploads="libraryUploads" />
      <div class="file-manager-body">
        <div class="file-manager-grid-wrap">
          <p class="text-muted" v-if="!loading && !items.length">{{ q || usageFilter !== 'all' ? _('No images match the search.') : _('No images have been uploaded yet. Drop images here or send them with the button above.') }}</p>
          <ul class="file-manager-grid">
            <li v-for="item in items" :key="item.id" :class="tileClass(item)" v-on:click="selectItem(item)" :title="item.filename">
              <span class="file-manager-thumb">
                <img loading="lazy" :src="thumbnailUrl(item)" :alt="item.title || item.filename" />
                <span class="file-manager-check" v-if="isPicker && isSelected(item)"><i class="fa fa-fw fa-check"></i></span>
              </span>
              <span class="file-manager-name">{{ item.title || item.filename }}</span>
              <span class="label label-default file-manager-unused" v-if="!item.usage_count">{{ _('Not in use') }}</span>
            </li>
          </ul>
          <div ref="sentinel" class="file-manager-sentinel"></div>
          <p class="text-center text-muted" v-if="loading"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
        </div>
        <div class="file-manager-details" v-if="active && !isPicker">
          <div class="file-manager-details-header">
            <h4 :title="active.filename">{{ active.title || active.filename }}</h4>
            <a href="javascript:;" v-on:click="active = null" :title="_('Close')"><i class="fa fa-fw fa-times"></i></a>
          </div>
          <a :href="imageUrl(active, 1600)" target="_blank" class="thumbnail">
            <img class="img-responsive" :src="imageUrl(active, 800)" />
          </a>
          <dl class="file-manager-facts">
            <dt>{{ _('File name') }}</dt>
            <dd>{{ active.filename }}</dd>
            <dt>ID</dt>
            <dd>{{ active.id }}</dd>
            <template v-if="active.description">
              <dt>{{ _('Description') }}</dt>
              <dd>{{ active.description }}</dd>
            </template>
          </dl>
          <div class="file-manager-actions">
            <button type="button" class="btn btn-sm btn-default" v-on:click="editDetails"><i class="fa fa-fw fa-edit"></i> {{ _('Details') }}</button>
            <button type="button" class="btn btn-sm btn-default" v-on:click="copyUrl"><i class="fa fa-fw fa-link"></i> {{ _('Copy URL') }}</button>
            <button type="button" class="btn btn-sm btn-danger" v-on:click="deleteActive" :disabled="usageLoading || usage.length > 0" :title="usage.length ? _('Remove the image from where it is used first.') : ''"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
          </div>
          <h4>{{ _('Used in') }}</h4>
          <p class="text-muted" v-if="usageLoading"><i class="fa fa-fw fa-cog fa-spin"></i></p>
          <p class="text-muted" v-else-if="!usage.length">{{ _('The image is not in use.') }}</p>
          <ul class="file-manager-usage" v-else>
            <li v-for="(row, index) in usage" :key="index">
              <a href="javascript:;" v-if="canOpen(row)" v-on:click="openUsage(row)">{{ usageName(row) }}</a>
              <span v-else>{{ usageName(row) }}</span>
              <span class="text-muted"> · {{ roleName(row) }}</span>
              <span class="label label-default" v-if="row.deleted">{{ _('Deleted') }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div class="file-manager-pick-footer" v-if="isPicker">
        <div class="file-manager-pick-usage" v-if="selected.length">
          <strong>{{ _('Used in') }}</strong>
          <div v-for="item in selected" :key="item.id">
            <span class="file-manager-pick-usage-name" v-if="selected.length > 1" :title="item.filename">{{ item.title || item.filename }}</span>
            <span class="text-muted" v-if="!pickUsage[item.id]"><i class="fa fa-fw fa-cog fa-spin"></i></span>
            <span class="text-muted" v-else-if="!pickUsage[item.id].length">{{ _('The image is not in use.') }}</span>
            <ul class="file-manager-usage" v-else>
              <li v-for="(row, index) in pickUsage[item.id]" :key="index">
                {{ usageName(row) }}<span class="text-muted"> · {{ roleName(row) }}</span>
                <span class="label label-default" v-if="row.deleted">{{ _('Deleted') }}</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="file-manager-pick-buttons">
          <button type="button" class="btn btn-default" v-on:click="$emit('close')">{{ _('Cancel') }}</button>
          <button type="button" class="btn btn-success" :disabled="!selected.length" v-on:click="pick"><i class="fa fa-fw fa-check"></i> {{ pickButtonText }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import debounce from 'lodash/debounce'
import components from '~/lib/components'
import { enqueue } from '~/lib/uploadqueue'
import { johkuEmbedDisplaySrc } from '~/lib/tiptap/johkuEmbedImage'
import { FileCaption } from '#components'

const PAGE_SIZE = 60

// The shop's images. Opened from the settings cog menu, and as an image picker
// from the editors (pickMode 'single' or 'multiple', see modals/FileManagerModal).
export default {
  mixins: [components],
  props: {
    pickMode: { type: String, default: null },
    maxPick: { type: Number, default: 0 }
  },
  emits: ['pick', 'close'],
  data () {
    return {
      items: [],
      total: 0,
      loading: false,
      q: '',
      usageFilter: 'all',
      sort: 'id',
      active: null,
      usage: [],
      usageLoading: false,
      selected: [],
      pickUsage: {},
      dragOver: false,
      observer: null,
      requestId: 0
    }
  },
  computed: {
    isPicker () {
      return !!this.pickMode
    },
    hasMore () {
      return this.items.length < this.total
    },
    libraryUploads () {
      return this.$store.state.uploads.filter(u => u.shopId === this.shopId && u.owner === null && u.status !== 'done')
    },
    pickButtonText () {
      if (this.pickMode === 'multiple' && this.selected.length > 1) {
        return this._('Choose %d images').replace('%d', this.selected.length)
      }
      return this._('Choose')
    }
  },
  watch: {
    q: debounce(function () {
      this.load(true)
    }, 300),
    usageFilter () {
      this.load(true)
    },
    sort () {
      this.load(true)
    }
  },
  methods: {
    // All images come from the image CDN, so browsing the files doesn't load the backend
    imageUrl (item, width = 400) {
      return johkuEmbedDisplaySrc(this.baseDomain + this.shopId, item.id, width)
    },
    thumbnailUrl (item) {
      return this.imageUrl(item, 400)
    },
    tileClass (item) {
      let className = 'file-manager-tile'
      if ((this.active && this.active.id === item.id) || this.isSelected(item)) {
        className += ' file-manager-tile-active'
      }
      return className
    },
    async load (reset = false) {
      if (reset) {
        this.items = []
        this.total = 0
      }
      const requestId = ++this.requestId
      this.loading = true
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        offset: this.items.length,
        sort: this.sort,
        version: this.version || '',
        apikey: this.apiKey
      })
      if (this.q) {
        params.set('q', this.q)
      }
      if (this.usageFilter !== 'all') {
        params.set('usage', this.usageFilter)
      }
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/files.json?' + params.toString())
      } catch (e) {
        if (requestId === this.requestId) {
          this.loading = false
          this.showError(e)
        }
        return
      }
      if (requestId !== this.requestId) {
        return // A newer search has started
      }
      const known = new Set(this.items.map(i => i.id))
      this.items.push(...response.data.items.filter(i => !known.has(i.id)))
      this.total = response.data.total
      this.loading = false
      this.$nextTick(() => this.loadMoreIfVisible())
    },
    loadMoreIfVisible () {
      const sentinel = this.$refs.sentinel
      if (!sentinel || this.loading || !this.hasMore) {
        return
      }
      const rect = sentinel.getBoundingClientRect()
      if (rect.top < window.innerHeight + 200) {
        this.load()
      }
    },
    isSelected (item) {
      return this.selected.some(s => s.id === item.id)
    },
    selectItem (item) {
      if (this.isPicker) {
        if (this.isSelected(item)) {
          this.selected = this.selected.filter(s => s.id !== item.id)
        } else if (this.pickMode === 'single') {
          this.selected = [item]
        } else if (!this.maxPick || this.selected.length < this.maxPick) {
          this.selected = [...this.selected, item]
        } else {
          this.showWarning(this._('You can add at most %d more images.').replace('%d', this.maxPick))
        }
        if (this.isSelected(item)) {
          this.loadPickUsage(item)
        }
        return
      }
      this.active = item
      this.loadUsage(item)
    },
    pick () {
      this.$emit('pick', this.selected.map(item => ({ id: item.id, ext: item.ext, filename: item.filename, title: item.title })))
    },
    async loadUsage (item) {
      this.usage = []
      this.usageLoading = true
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/files/' + item.id + '/usage.json?apikey=' + this.apiKey)
        if (this.active && this.active.id === item.id) {
          this.usage = response.data || []
          item.usage_count = this.usage.length
        }
      } catch (e) {
        this.showError(e)
      }
      this.usageLoading = false
    },
    // Where the images chosen in the picker are used, listed in the footer
    async loadPickUsage (item) {
      if (this.pickUsage[item.id]) {
        return
      }
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/files/' + item.id + '/usage.json?apikey=' + this.apiKey)
        this.pickUsage = { ...this.pickUsage, [item.id]: response.data || [] }
      } catch (e) {
        this.showError(e)
      }
    },
    usageName (row) {
      switch (row.type) {
        case 'product':
          return this._('Product') + ' ' + row.id + (row.name ? ': ' + row.name : '')
        case 'category':
          return this._('Category') + ' ' + row.id + (row.name ? ': ' + row.name : '')
        case 'page':
          return this._('Page') + ' ' + row.id + (row.name ? ': ' + row.name : '')
        case 'component':
          return this._('Component') + ' ' + row.id + (row.name ? ': ' + row.name : '')
        case 'template':
          return this._('Template') + ' ' + row.id + (row.name ? ': ' + row.name : '')
        case 'settings':
          return this._('Company profile')
        case 'receipt':
          return this._('Receipts') + ' (' + row.count + ')'
      }
      return row.type
    },
    roleName (row) {
      switch (row.role) {
        case 'main':
          return this._('Main image')
        case 'mobile':
          return this._('Mobile main image')
        case 'gallery':
          return this._('Additional images')
        case 'embedded':
          return this._('In description')
        case 'background':
          return this._('Background')
        case 'logo':
          return this._('Logo')
        case 'storefrontlogo':
          return this._('Storefront logo')
        case 'profile':
          return this._('Profile image')
        case 'mainimage':
          return this._('Main image')
      }
      return row.role
    },
    canOpen (row) {
      return ['product', 'category', 'page', 'component', 'template', 'settings'].includes(row.type) && !row.deleted
    },
    openUsage (row) {
      if (row.type === 'settings') {
        this.EventBus.$emit('showComponent', {
          shopId: this.shopId,
          component: 'CompanyProfile',
          title: this._('Company profile'),
          initData: { activateTab: 'images' }
        })
        return
      }
      const editors = {
        product: ['ProductDetails', 'productId', this._('Product')],
        category: ['CategoryDetails', 'categoryId', this._('Category')],
        page: ['PageDetails', 'pageId', this._('Page')],
        component: ['ComponentDetails', 'componentId', this._('Component')],
        template: ['TemplateDetails', 'templateId', this._('Template')]
      }
      const [component, key, title] = editors[row.type]
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component,
        title: title + ' ' + row.id + (row.name ? ': ' + row.name : ''),
        width: 6,
        data: {
          [key]: row.id,
          activeTab: row.role === 'embedded' ? 'general' : 'images',
          trail: this.componentTrail
        }
      })
    },
    async editDetails () {
      const item = this.active
      let file
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/files/' + item.id + '.json?version=' + (this.version || '') + '&apikey=' + this.apiKey)
        file = response.data
      } catch (e) {
        return this.showError(e)
      }
      this.showModal('filecaption', FileCaption, {
        props: {
          defaultTitle: file.title,
          defaultDescription: file.description,
          defaultVideo: file.video,
          defaultSmartCrop: file.smartcrop || 'default',
          source: item,
          settings: this.settings
        },
        events: {
          save: (data) => this.saveDetails(item, data)
        }
      })
    },
    async saveDetails (item, data) {
      try {
        await axios.put(this.baseDomain + this.shopId + '/files/' + item.id + '.json?version=' + (this.version || '') + '&apikey=' + this.apiKey, {
          id: item.id,
          title: data.title,
          description: data.description,
          video: data.video,
          smartcrop: data.smartcrop
        })
      } catch (e) {
        return this.showError(e)
      }
      item.title = data.title
      item.description = data.description
      this.showNotification(this._('Changes saved successfully!'))
    },
    async copyUrl () {
      try {
        await navigator.clipboard.writeText(this.imageUrl(this.active, 1600))
        this.showNotification(this._('Copied to the clipboard.'))
      } catch (e) {
        this.showError(e)
      }
    },
    deleteActive () {
      const item = this.active
      this.showDialog({
        title: this._('Delete the image?'),
        text: this._('The image is removed from the shop for good.'),
        buttons: [
          { title: this._('Cancel'), icon: 'ban', handler: () => {} },
          { title: this._('Delete'), class: 'danger', icon: 'trash', handler: () => this.deleteItem(item) }
        ]
      })
    },
    async deleteItem (item) {
      try {
        await axios.delete(this.baseDomain + this.shopId + '/files/' + item.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        if (e.response && e.response.status === 409) {
          this.showWarning(this._('The image is in use and cannot be deleted.'))
          this.loadUsage(item)
          return
        }
        return this.showError(e)
      }
      this.items = this.items.filter(i => i.id !== item.id)
      this.total--
      if (this.active && this.active.id === item.id) {
        this.active = null
      }
      this.showNotification(this._('Image deleted.'))
    },
    upload (files) {
      for (const file of files.filter(f => f.type.startsWith('image/'))) {
        enqueue(file, { owner: null, name: file.name })
      }
    },
    onSelect (e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      this.upload(files)
    },
    onDragOver (e) {
      if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
        e.preventDefault()
        this.dragOver = true
      }
    },
    onDrop (e) {
      if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) {
        return
      }
      e.preventDefault()
      this.dragOver = false
      this.upload(Array.from(e.dataTransfer.files))
    },
    // Images uploaded here, or from an editor, show up at the top of the newest-first list
    onUploadCompleted (message) {
      if (!message || message.shopId !== this.shopId || this.sort !== 'id' || this.q) {
        return
      }
      const used = message.owner !== null
      if ((this.usageFilter === 'unused' && used) || (this.usageFilter === 'used' && !used)) {
        return
      }
      if (this.items.some(i => i.id === message.file.id)) {
        return
      }
      this.items.unshift({
        id: message.file.id,
        filename: message.file.filename,
        ext: message.file.ext,
        title: '',
        description: '',
        usage_count: message.owner === null ? 0 : 1
      })
      this.total++
    }
  },
  mounted () {
    this.load(true)
    this.EventBus.$on('uploadCompleted', this.onUploadCompleted)
    if (typeof IntersectionObserver !== 'undefined' && this.$refs.sentinel) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) {
          this.loadMoreIfVisible()
        }
      }, { rootMargin: '200px' })
      this.observer.observe(this.$refs.sentinel)
    }
  },
  beforeUnmount () {
    this.EventBus.$off('uploadCompleted', this.onUploadCompleted)
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped>
.file-manager-picker {
  padding: 15px;
}
.file-manager-dragover {
  outline: 2px dashed #5cb85c;
  outline-offset: -4px;
}
.file-manager-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.file-manager-search {
  flex: 1 1 200px;
}
.file-manager-filter,
.file-manager-sort {
  flex: 0 1 160px;
}
.file-manager-upload input[type=file] {
  display: none;
}
.file-manager-body {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}
.file-manager-grid-wrap {
  flex: 1 1 360px;
  min-width: 0;
}
.file-manager-picker .file-manager-grid-wrap {
  max-height: 60vh;
  overflow-y: auto;
}
.file-manager-grid {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}
.file-manager-tile {
  position: relative;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 4px;
  padding: 4px;
  min-width: 0;
}
.file-manager-tile:hover {
  border-color: #ddd;
}
.file-manager-tile-active,
.file-manager-tile-active:hover {
  border-color: #337ab7;
}
.file-manager-thumb {
  position: relative;
  display: block;
  aspect-ratio: 1 / 1;
  background: #f5f5f5;
  overflow: hidden;
}
.file-manager-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.file-manager-check {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #337ab7;
  color: #fff;
  border-radius: 50%;
  padding: 2px;
}
.file-manager-name {
  display: block;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 4px;
}
.file-manager-unused {
  position: absolute;
  left: 8px;
  top: 8px;
  font-size: 10px;
}
.file-manager-sentinel {
  height: 1px;
}
.file-manager-details {
  flex: 0 1 320px;
  min-width: 260px;
  border-left: 1px solid #eee;
  padding-left: 16px;
}
.file-manager-details-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.file-manager-details-header h4 {
  flex: 1;
  min-width: 0;
  margin-top: 0;
  overflow-wrap: anywhere;
}
.file-manager-facts dd {
  margin-bottom: 6px;
  overflow-wrap: anywhere;
}
.file-manager-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.file-manager-usage {
  padding-left: 18px;
}
.file-manager-pick-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  border-top: 1px solid #eee;
  padding-top: 12px;
}
.file-manager-pick-usage {
  flex: 1 1 260px;
  min-width: 0;
  max-height: 20vh;
  overflow-y: auto;
  overflow-wrap: anywhere;
}
.file-manager-pick-usage .file-manager-usage {
  margin-bottom: 4px;
}
.file-manager-pick-usage-name {
  display: block;
  font-weight: 600;
  margin-top: 4px;
}
.file-manager-pick-buttons {
  display: flex;
  gap: 8px;
  margin-left: auto;
}
</style>
